import { DB_TYPE } from '@/common/define/common.define';
import { type AddTabPanelInfo, TOTAL_DETAIL } from '@/common/stores/slide-detail.logic';
import type { DetailParams } from './types';
import {
  getAltibaseReplicationDetailAddition,
  getMysqlObjectDetailAddition,
  getMysqlSlowQueryAddition,
  getPostgresqlObjectDetailAddition,
  getCubridBrokerDetailAddition,
  getTransactionAddition,
  getAltibaseReplicationObjectDetailAddition,
} from './db';
import { InvalidDatabaseTypeError } from './error.utils';

type Params = DetailParams & { dbType?: string };

export const useMiscellaneousDetail = () => {
  const getObjectDetailAddition = (
    params: Params,
  ): Extract<AddTabPanelInfo, { type: typeof TOTAL_DETAIL.OBJECT }> => {
    const { dbType = '' } = params;

    const additionGetterFn = {
      [DB_TYPE.POSTGRESQL]: getPostgresqlObjectDetailAddition,
      [DB_TYPE.MYSQL]: getMysqlObjectDetailAddition,
    }[dbType];

    if (!additionGetterFn) {
      throw new InvalidDatabaseTypeError({ dbType });
    }

    const { name, addition } = additionGetterFn(params);
    return {
      type: TOTAL_DETAIL.OBJECT,
      name,
      addition,
    };
  };

  const getSlowQueryAddition = (
    params: Params,
  ): Extract<AddTabPanelInfo, { type: typeof TOTAL_DETAIL.SLOW_QUERY }> => {
    const { dbType = '' } = params;

    const additionGetterFn = {
      [DB_TYPE.MYSQL]: getMysqlSlowQueryAddition,
    }[dbType];

    if (!additionGetterFn) {
      throw new InvalidDatabaseTypeError({ dbType });
    }

    const { name, addition } = additionGetterFn(params);
    return {
      type: TOTAL_DETAIL.SLOW_QUERY,
      name,
      addition,
    };
  };

  const getReplicationDetailAddition = (
    params: Params,
  ): Extract<AddTabPanelInfo, { type: typeof TOTAL_DETAIL.REPLICATION }> => {
    const { dbType = '' } = params;

    const additionGetterFn = {
      [DB_TYPE.ALTIBASE]: getAltibaseReplicationDetailAddition,
    }[dbType];

    if (!additionGetterFn) {
      throw new InvalidDatabaseTypeError({ dbType });
    }

    const { name, addition } = additionGetterFn(params);
    return {
      type: TOTAL_DETAIL.REPLICATION,
      name,
      addition,
    };
  };

  const getReplicationObjectDetailAddition = (
    params: Params,
  ): Extract<AddTabPanelInfo, { type: typeof TOTAL_DETAIL.REPLICATION_OBJECT }> => {
    const { dbType = '' } = params;

    const additionGetterFn = {
      [DB_TYPE.ALTIBASE]: getAltibaseReplicationObjectDetailAddition,
    }[dbType];

    if (!additionGetterFn) {
      throw new InvalidDatabaseTypeError({ dbType });
    }

    const { addition } = additionGetterFn(params);
    return {
      type: TOTAL_DETAIL.REPLICATION_OBJECT,
      name: '', // 없는 스펙
      addition,
    };
  };

  const getBrokerDetailAddition = (
    params: Params,
  ): Extract<AddTabPanelInfo, { type: typeof TOTAL_DETAIL.BROKER }> => {
    const { dbType = '' } = params;

    const additionGetterFn = {
      [DB_TYPE.CUBRID]: getCubridBrokerDetailAddition,
    }[dbType];

    if (!additionGetterFn) {
      throw new InvalidDatabaseTypeError({ dbType });
    }

    const { name, addition } = additionGetterFn(params);
    return {
      type: TOTAL_DETAIL.BROKER,
      name,
      addition,
    };
  };

  const getTransactionDetailAddition = (
    params: Params,
  ): Extract<AddTabPanelInfo, { type: typeof TOTAL_DETAIL.TRANSACTION }> => {
    const { name, transaction } = getTransactionAddition(params);
    return {
      type: TOTAL_DETAIL.TRANSACTION,
      name,
      transaction,
    };
  };

  return {
    getObjectDetailAddition,
    getSlowQueryAddition,
    getBrokerDetailAddition,
    getTransactionDetailAddition,
    getReplicationDetailAddition,
    getReplicationObjectDetailAddition,
  };
};
